import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Paper,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Security as SecurityIcon, Autorenew, Visibility, VisibilityOff } from "@mui/icons-material";

// Password generator helper
const easyWords = [
  "Welcome",
  "Table",
  "Water",
  "Garden",
  "Rocket",
  "Summer",
  "Winter",
  "Coffee",
  "Laptop",
  "Orange",
  "River",
  "Forest",
  "Travel",
  "Dinner",
  "Music",
  "Dream",
  "Ocean",
  "Light",
  "Happy",
  "Magic",
];
const specialChars = ["#", "@", "!", "$", "%", "*"];
const numbers = ["12", "2024", "99", "42", "77", "88", "01", "09", "33", "55"];

const generateEasyPassword = () => {
  const word = easyWords[Math.floor(Math.random() * easyWords.length)];
  const char = specialChars[Math.floor(Math.random() * specialChars.length)];
  const num = numbers[Math.floor(Math.random() * numbers.length)];
  let mixedWord = word;
  if (word.length > 2 && Math.random() > 0.5) {
    const idx = Math.floor(Math.random() * (word.length - 2)) + 1;
    mixedWord = word.slice(0, idx) + (Math.random() > 0.5 ? word[idx].toLowerCase() : word[idx].toUpperCase()) + word.slice(idx + 1);
  }
  return `${mixedWord}${char}${num}`;
};

const ChangePassword = () => {
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showCopyAlert, setShowCopyAlert] = useState(false);

  const handlePasswordInputChange = (field, value) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
    setPasswordError("");
    setPasswordSuccess(false);
  };

  const handlePasswordSubmit = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError("Please fill in all fields");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New password and confirmation do not match");
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters long");
      return;
    }
    try {
      setPasswordLoading(true);
      setPasswordError("");
      const token = sessionStorage.getItem("accessToken");
      if (!token) {
        setPasswordError("Authentication token not found. Please log in again.");
        return;
      }
      const response = await fetch("/api/manage/info", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          newEmail: "",
          newPassword: passwordForm.newPassword,
          oldPassword: passwordForm.currentPassword,
        }),
      });
      if (response.ok) {
        setPasswordSuccess(true);
        setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      } else {
        const errorData = await response.json().catch(() => ({}));
        setPasswordError(errorData.message || `Failed to update password (${response.status}). Please check your current password.`);
      }
    } catch (err) {
      setPasswordError("Failed to update password. Please try again.");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePasswordCancel = () => {
    setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setPasswordError("");
    setPasswordSuccess(false);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: "bold" }}>
        <SecurityIcon sx={{ mr: 1, verticalAlign: "middle" }} />
        Change Password
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                Update Your Password
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Choose a strong password that you haven't used before. It should be at least 8 characters long.
              </Typography>
              {passwordError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {passwordError}
                </Alert>
              )}
              {passwordSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Password updated successfully!
                </Alert>
              )}
              {showCopyAlert && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Password copied to clipboard!
                </Alert>
              )}
              <Box component="form" sx={{ mt: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Current Password
                    </Typography>
                    <TextField
                      type="password"
                      placeholder="Enter your current password"
                      value={passwordForm.currentPassword}
                      onChange={(e) => handlePasswordInputChange("currentPassword", e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      New Password
                    </Typography>
                    <TextField
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Enter your new password"
                      value={passwordForm.newPassword}
                      onChange={(e) => handlePasswordInputChange("newPassword", e.target.value)}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <>
                            <IconButton
                              aria-label="Copy password"
                              onClick={() => {
                                if (passwordForm.newPassword) {
                                  navigator.clipboard.writeText(passwordForm.newPassword);
                                  setShowCopyAlert(true);
                                  setTimeout(() => setShowCopyAlert(false), 2000);
                                }
                              }}
                              edge="end"
                              tabIndex={0}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                width="24"
                                height="24"
                              >
                                <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2" />
                                <rect x="3" y="3" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2" />
                              </svg>
                            </IconButton>
                            <IconButton
                              aria-label="Generate password"
                              onClick={() => {
                                const pwd = generateEasyPassword();
                                handlePasswordInputChange("newPassword", pwd);
                                handlePasswordInputChange("confirmPassword", pwd);
                                setShowNewPassword(true);
                              }}
                              edge="end"
                              tabIndex={0}
                            >
                              <Autorenew />
                            </IconButton>
                            <IconButton
                              aria-label={showNewPassword ? "Hide password" : "Show password"}
                              onClick={() => setShowNewPassword((show) => !show)}
                              edge="end"
                              tabIndex={0}
                            >
                              {showNewPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Confirm New Password
                    </Typography>
                    <TextField
                      type="password"
                      placeholder="Confirm your new password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => handlePasswordInputChange("confirmPassword", e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Button variant="outlined" size="large" onClick={handlePasswordCancel} disabled={passwordLoading}>
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        size="large"
                        color="primary"
                        onClick={handlePasswordSubmit}
                        disabled={passwordLoading}
                      >
                        {passwordLoading ? (
                          <>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Updating...
                          </>
                        ) : (
                          "Update Password"
                        )}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card elevation={1}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Password Requirements
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                  At least 8 characters long
                </Typography>
                <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                  Contains uppercase and lowercase letters
                </Typography>
                <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                  Contains at least one number
                </Typography>
                <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                  Contains at least one special character
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ChangePassword;
